import React from 'react';
import { FiX, FiDownload, FiUser, FiCalendar, FiImage, FiFileText } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';

const EvidenceModal = ({ isOpen, onClose, evidence }) => {
    if (!isOpen || !evidence) return null;

    const downloadEvidence = () => {
        const element = document.createElement("a");
        const file = new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json' });
        element.href = URL.createObjectURL(file);
        element.download = `evidence-report-${evidence.reportId}.json`;
        document.body.appendChild(element);
        element.click();
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-start">
                            <h3 className="text-xl leading-6 font-bold text-gray-900" id="modal-title">
                                Preserved Evidence
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                                <FiX className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="mt-4 border-t border-gray-200 pt-4">
                            {/* Metadata */}
                            <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                                <div>
                                    <span className="block text-xs text-gray-500 uppercase tracking-wide">Report ID</span>
                                    <span className="block text-sm font-medium text-gray-900">{evidence.reportId}</span>
                                </div>
                                <div>
                                    <span className="block text-xs text-gray-500 uppercase tracking-wide">Preserved At</span>
                                    <span className="block text-sm font-medium text-gray-900 flex items-center">
                                        <FiCalendar className="mr-1 h-3 w-3" />
                                        {new Date(evidence.preservedAt).toLocaleString()}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-xs text-gray-500 uppercase tracking-wide">Reason</span>
                                    <span className="block text-sm font-medium text-red-600">{evidence.reason}</span>
                                </div>
                                {evidence.ncmecReportId && (
                                    <div>
                                        <span className="block text-xs text-gray-500 uppercase tracking-wide">NCMEC Case ID</span>
                                        <span className="block text-sm font-mono font-bold text-blue-600">{evidence.ncmecReportId}</span>
                                    </div>
                                )}
                            </div>

                            {/* Author Details */}
                            <div className="mb-6">
                                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                                    <FiUser className="mr-2" /> Author Details (At time of report)
                                </h4>
                                <div className="bg-white border border-gray-200 rounded-md p-3 text-sm">
                                    <p><span className="font-semibold">Name:</span> {evidence.author?.name}</p>
                                    <p><span className="font-semibold">Email:</span> {evidence.author?.email}</p>
                                    <p><span className="font-semibold">User ID:</span> {evidence.author?.id}</p>
                                    <p><span className="font-semibold">IP Address:</span> {evidence.author?.ip || 'N/A'}</p>
                                </div>
                            </div>

                            {/* Content */}
                            <div>
                                <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                                    <FiFileText className="mr-2" /> Content Snapshot
                                </h4>
                                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                                    <p className="whitespace-pre-wrap text-gray-800 font-serif text-lg">{evidence.content}</p>
                                </div>

                                {evidence.image && (
                                    <div className="mb-4">
                                        <h5 className="text-xs font-semibold text-gray-500 mb-2 flex items-center">
                                            <FiImage className="mr-1" /> Attached Media
                                        </h5>
                                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex justify-center p-2">
                                            <img
                                                src={evidence.image.startsWith('http') ? evidence.image : buildApiUrl(evidence.image)}
                                                alt="Evidence"
                                                className="max-h-96 object-contain"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between">
                        <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                            onClick={downloadEvidence}
                        >
                            <FiDownload className="mr-2 h-5 w-5 text-gray-500" />
                            Download Package
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                            onClick={onClose}
                        >
                            Close Viewer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EvidenceModal;
